import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  version = 'beta_1.1.0';

  constructor() {}

  get(key: string, parse: boolean = false) {
    try {
      const value = localStorage.getItem(key);

      if (value) {
        return parse ? JSON.parse(value) : value;
      }

      return undefined;
    } catch (error) {
      return undefined;
    }
  }

  set(key: string, value: any, parse: boolean = false) {
    try {
      let parsedValue = parse ? JSON.stringify(value) : value;

      localStorage.setItem(key, parsedValue);

      return true;
    } catch (error) {
      return undefined;
    }
  }

  remove(key: string) {
    try {
      localStorage.removeItem(key);

      return true;
    } catch (error) {
      return undefined;
    }
  }

  logout(): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        // Lista completa de todas as chaves que precisam ser limpas
        const keysToRemove = [
          '@app:token',
          '@app:user',
          `@app:first_visit@${this.version}`,
          '@app:preuser',
          '@app:permissions',
          'permissions',
          'bond',
          'permission_type',
          'edit_user',
          'new_user_data',
          'filters',
          '@app:dashboard_permissions_onboarding_viewed',
          '@app:pending_confirmation_modal_seen',
          '@app:pending_confirmation_modal_seen_v2',
          'last_route',
          'past_url',
          'signup_basic_personal',
          'breadcrumb_history',
          'selected_role_id',
          // Limpar também chaves de formulários complementares
          'complementaryAddress',
          'complementaryBank',
          'complementaryComposition',
          'complementaryEconomicForm',
          'complementaryGeneral',
          'complementaryRepresentatives',
          'powers',
          'relevancePowers',
          'complementaryProfessional',
          'haveRepresentative',
        ];

        // Remover todas as chaves
        keysToRemove.forEach((key) => {
          localStorage.removeItem(key);
        });

        // Usar requestAnimationFrame para garantir que o navegador processou as remoções
        requestAnimationFrame(() => {
          // Verificar se todas as chaves críticas foram removidas
          const criticalKeys = ['@app:user', '@app:token', 'permissions'];
          let allCleared = true;

          for (const key of criticalKeys) {
            if (localStorage.getItem(key) !== null) {
              allCleared = false;
              // Tentar remover novamente
              localStorage.removeItem(key);
            }
          }

          // Se ainda não estiver tudo limpo, tentar mais uma vez
          if (!allCleared) {
            setTimeout(() => {
              criticalKeys.forEach((key) => {
                if (localStorage.getItem(key) !== null) {
                  localStorage.removeItem(key);
                }
              });
              resolve(true);
            }, 10);
          } else {
            resolve(true);
          }
        });
      } catch (error) {
        console.error('Erro ao limpar localStorage:', error);
        resolve(false);
      }
    });
  }

  clearForm() {
    try {
      localStorage.removeItem('complementaryAddress');
      localStorage.removeItem('complementaryBank');
      localStorage.removeItem('complementaryComposition');
      localStorage.removeItem('complementaryEconomicForm');
      localStorage.removeItem('complementaryGeneral');
      localStorage.removeItem('complementaryRepresentatives');
      localStorage.removeItem('powers');
      localStorage.removeItem('relevancePowers');
      localStorage.removeItem('complementaryProfessional');
      localStorage.removeItem('haveRepresentative');

      return true;
    } catch (error) {
      return undefined;
    }
  }

  clear() {
    localStorage.clear();
  }

  setToken(token: string) {
    return this.set('@app:token', token);
  }

  getToken() {
    return this.get('@app:token');
  }

  setStep(step: string, data: any) {
    return this.set(`@app:step:${step}`, data, true);
  }

  getStep(step: string) {
    return this.get(`@app:step:${step}`, true);
  }

  setUser(user: any) {
    return this.set('@app:user', user, true);
  }

  getUser() {
    return this.get('@app:user', true);
  }

  setPermissionType(permissionType: string) {
    return this.set('permission_type', permissionType, true);
  }

  getPermissionType() {
    return this.get('permission_type', true);
  }

  setPreUser(preuser: any) {
    return this.set('@app:preuser', preuser, true);
  }

  getPreUser(): any {
    return this.get('@app:preuser', true);
  }

  setEditUser(ids: any) {
    return this.set('edit_user', ids, true);
  }

  getEditUser() {
    return this.get('edit_user', true);
  }

  setNewUserData(payload: any) {
    return this.set('new_user_data', payload, true);
  }

  getNewUserData() {
    return this.get('new_user_data', true);
  }

  setCompanyBondData(bond: any) {
    return this.set('bond', bond, true);
  }

  getCompanyBondData(): any {
    return this.get('bond', true);
  }

  setFilters(item: any) {
    return this.set('filters', item, true);
  }

  getFilters(): any {
    return this.get('filters', true);
  }

  setVisited() {
    return this.set(`@app:first_visit@${this.version}`, false);
  }

  getVisited() {
    return this.get(`@app:first_visit@${this.version}`);
  }

  // Métodos para sessionStorage (usado para dados temporários de formulários)
  getSession(key: string, parse: boolean = false) {
    try {
      const value = sessionStorage.getItem(key);

      if (value) {
        return parse ? JSON.parse(value) : value;
      }

      return undefined;
    } catch (error) {
      return undefined;
    }
  }

  setSession(key: string, value: any, parse: boolean = false) {
    try {
      let parsedValue = parse ? JSON.stringify(value) : value;

      sessionStorage.setItem(key, parsedValue);

      return true;
    } catch (error) {
      return undefined;
    }
  }

  removeSession(key: string) {
    try {
      sessionStorage.removeItem(key);

      return true;
    } catch (error) {
      return undefined;
    }
  }
}
